import { useEffect, useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import ArticleCard from '@/components/base/ArticleCard';
import StatusBlock from '@/components/base/StatusBlock';
import { useAsyncData } from '@/hooks/useAsyncData';
import { getAllArticles, sortArticles, SORT_OPTIONS, type SortKey } from '@/lib/content';
import type { Article } from '@/types/content';

const PAGE_SIZE = 6;

export default function NewsPage() {
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  const { data, loading, error, reload } = useAsyncData<Article[]>(
    () => sortArticles(getAllArticles(), sort),
    [sort],
  );

  useEffect(() => {
    setPage(1);
  }, [sort]);

  const total = data?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentList = data ? data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : [];

  const goPage = (p: number) => {
    setPage(Math.min(Math.max(1, p), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <PageHeader
        eyebrow="NEWS LIST"
        title="最新影视资讯"
        description="汇集《疯狂试爱2》的官方动态、行业观察与专题报道，支持按发布时间与热度排序浏览。"
        crumbs={[{ label: '首页', to: '/' }, { label: '资讯列表' }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            共收录 <strong className="font-heading text-base text-primary-600">{total}</strong> 条资讯内容
          </p>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSort(option.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  sort === option.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-4 rounded-lg border border-background-200 p-4 sm:flex-row">
                <div className="h-40 w-full animate-pulse rounded-md bg-background-100 sm:h-32 sm:w-48"></div>
                <div className="flex-1 space-y-3 py-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-background-100"></div>
                  <div className="h-3 w-full animate-pulse rounded bg-background-100"></div>
                  <div className="h-3 w-1/2 animate-pulse rounded bg-background-100"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <StatusBlock icon="ri-wifi-off-line" title="网络异常" description={error} actionLabel="重新加载" onAction={reload} />
        )}

        {!loading && !error && data && data.length === 0 && (
          <StatusBlock
            icon="ri-file-search-line"
            title="未找到相关资讯"
            description="当前筛选条件下没有匹配的内容，试试重置筛选条件。"
            actionLabel="重置筛选"
            onAction={() => setSort('latest')}
          />
        )}

        {!loading && !error && currentList.length > 0 && (
          <>
            <div className="space-y-4">
              {currentList.map((article) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => goPage(page - 1)}
                  disabled={page === 1}
                  className="flex h-10 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goPage(index + 1)}
                    className={`flex h-10 w-10 items-center justify-center rounded-md text-sm font-semibold transition-colors cursor-pointer ${
                      page === index + 1
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => goPage(page + 1)}
                  disabled={page === totalPages}
                  className="flex h-10 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  下一页
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}